import jsPDF from 'jspdf';
import { IDataverseQuote, IQuoteLineItem, ISubQuoteProduct } from "@/features/quotes-management/types";
import { getSubQuoteProductsApi, getQuoteImageApi } from '@/features/quotes-management/api/dataverse';
import { getProductSetupDetail } from '@/features/product-configurator/api/productsSetup';
import { formatDealerAmount } from '@/utils/currency';
import { MiddlewareAPI } from '@/lib/axios';
import { FOIL_CHARGE_BREAKDOWN_LABEL, FOIL_COLOUR_OPTION_TYPES, OPTION_TYPE_EGRESS_COMPLAINT, OPTION_TYPE_SASH } from '@/features/assistant/constants';
import { calculateFireEgress, calculateFireEgressMultiFrame, FireEgressWindowType, SashOperationValue } from '@/features/assistant/utils/fireEgress';
import { isProfileColourFoilOption } from '@/features/assistant/utils/profileColour';
import dayjs from 'dayjs';
import vsLogo from '@/assets/images/victorian-sliders-logo.jpg';

const getSubProductOptionType = (p: ISubQuoteProduct): number | null => {
  const raw = p.dogma_optiontype;
  if (raw !== undefined && raw !== null) {
    const n = Number(raw);
    if (!isNaN(n)) return n;
  }
  for (const field of [p.dogma_valuejson, p.dogma_value]) {
    try {
      const parsed = JSON.parse(field || '');
      if (parsed?.optionType !== undefined) return Number(parsed.optionType);
    } catch {}
  }
  if (p.dogma_optionname === 'Variation') return 64;
  return null;
};

const filterVariationItems = (items: ISubQuoteProduct[]): ISubQuoteProduct[] => {
  const hasVariation = items.some(p => getSubProductOptionType(p) === 64);
  return items.filter(p => {
    const ot = getSubProductOptionType(p);
    // When variation exists: show variation (64), hide window type (0)
    // When no variation: show window type (0) only
    if (ot === 0 && hasVariation) return false;
    return true;
  });
};

export interface IPDFHeaderDates {
  productionDate?: string;
  deliveryDate?: string;
  processedDate?: string;
  printedDate?: string;
  orderDate?: string;
  evolutionJobNumber?: string;
}

/**
 * Fine-tuned Order Acknowledgement PDF Generator
 * Matches the original PDF design exactly with precise measurements
 */
/** Fetch an image URL and return a base64 data URI, or null on failure.
 *  Rejects file:/// URLs immediately — they are Android local paths, not web-accessible. */
const fetchImageAsDataUrl = async (url: string): Promise<string | null> => {
  if (!url || url.startsWith('file:///')) return null;
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

/** Fetch a quote image file via backend proxy (direct blob URL is CORS-blocked). */
const getQuoteImageFileDataUrlApi = async (quoteGuid: string, fileName: string): Promise<string | null> => {
  try {
    const response = await MiddlewareAPI.get('/GetQuoteImageFile', {
      params: { quoteGuid, fileName },
      responseType: 'arraybuffer',
    });
    const uint8Array = new Uint8Array(response.data);
    let mimeType = 'image/png';
    if (uint8Array[0] === 0xff && uint8Array[1] === 0xd8) mimeType = 'image/jpeg';
    else if (uint8Array[0] === 0x52 && uint8Array[1] === 0x49) mimeType = 'image/webp';
    else {
      const ext = fileName.split('.').pop()?.toLowerCase();
      if (ext === 'jpg' || ext === 'jpeg') mimeType = 'image/jpeg';
      else if (ext === 'webp') mimeType = 'image/webp';
    }
    const blob = new Blob([uint8Array], { type: mimeType });
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch { return null; }
};

/** Compress a data URL to JPEG at reduced size — keeps PDF file small.
 *  maxPx caps the longer dimension; quality 0–1. Returns original on failure. */
const compressImageForPdf = (dataUrl: string, maxPx = 250, quality = 0.55): Promise<string> =>
  new Promise((resolve) => {
    try {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxPx / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve(dataUrl); return; }
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = () => resolve(dataUrl);
      img.src = dataUrl;
    } catch {
      resolve(dataUrl);
    }
  });

/** Fetch product thumbnail from ProductApi using the line item's productid */
const fetchProductThumbnailFromApi = async (productId?: string): Promise<string | null> => {
  if (!productId) return null;
  try {
    const product = await getProductSetupDetail(productId);
    const imageUrl = product?.dogma_productimageurl;
    if (!imageUrl) return null;
    return await fetchImageAsDataUrl(imageUrl);
  } catch {
    return null;
  }
};

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type FireEgressStatus =
  | null
  | { compliant: true; pass: 'sliding' | 'tilting' | 'both' }
  | { compliant: false };

function getEgressBadgeForPDF(subProducts: ISubQuoteProduct[]): FireEgressStatus {
  // Check optionType 48 sub-product has "yes" value.
  // Fallback: dogma_optionname may store the question name (e.g. "Egress Compliant") instead of numeric optionType.
  const egressSub = subProducts.find((p) => {
    let ot = getSubProductOptionType(p);
    if (ot == null) {
      try {
        const vj = JSON.parse(p.dogma_valuejson || '') as any;
        if (vj?.optionType === OPTION_TYPE_EGRESS_COMPLAINT) ot = OPTION_TYPE_EGRESS_COMPLAINT;
      } catch {}
    }
    const isEgressByType = ot === OPTION_TYPE_EGRESS_COMPLAINT;
    const isEgressByName = /egress/i.test(p.dogma_optionname ?? '');
    if (!isEgressByType && !isEgressByName) return false;
    const raw = (p.dogma_value ?? p.dogma_name ?? '').toLowerCase();
    let displayVal = raw;
    try {
      const parsed = JSON.parse(p.dogma_value || '');
      const label = (typeof parsed === 'object' && parsed !== null)
        ? (parsed.label ?? parsed.name ?? parsed.optionText ?? parsed.value ?? '')
        : String(parsed);
      displayVal = label.toLowerCase();
    } catch {}
    return displayVal.includes('yes') || raw.includes('yes');
  });
  if (!egressSub) return null;

  const meas = extractMeasurements(subProducts);
  const windowHeight = meas.height;
  if (!windowHeight) return { compliant: false };

  // Detect window type from sub-product names/values.
  const isArch = subProducts.some((p) => {
    const text = ((p.dogma_name ?? '') + ' ' + (p.dogma_value ?? '') + ' ' + (p.dogma_optionname ?? '')).toLowerCase();
    return text.includes('arch') || text.includes('swept head');
  });
  const windowType: FireEgressWindowType = isArch ? 'arch'
    : (meas.frameWidths && meas.frameWidths.length > 1) ? 'couple'
    : 'standard';

  if (meas.frameWidths && meas.frameWidths.length > 1) {
    const multi = calculateFireEgressMultiFrame(meas.frameWidths, windowType, windowHeight, undefined, undefined, meas.frameSashOperations);
    if (!multi.anyCompliant) return { compliant: false };
    const firstPass = multi.frames.find((f) => f.result.applicable && (f.result as any).pass !== 'none');
    const pass = (firstPass?.result as any)?.pass ?? 'sliding';
    return { compliant: true, pass };
  }

  const windowWidth = meas.width ?? meas.frameWidths?.[0] ?? null;
  if (!windowWidth) return { compliant: false };

  const result = calculateFireEgress({ windowType, windowWidth, windowHeight, sashOperation: meas.sashOperation ?? undefined });
  if (!result.applicable || result.pass === 'none') return { compliant: false };
  return { compliant: true, pass: result.pass };
}

/** Extract the product thumbnail URL from sub-products.
 *  The real product option has a full UUID as optionId (not a short code like "A0002").
 *  Falls back to withoutHornsUrl / withHornsUrl if thumbnail is absent. */
const extractProductThumbnail = (subProducts: ISubQuoteProduct[]): string | null => {
  // Pass 1: UUID optionId + thumbnail / withoutHornsUrl / withHornsUrl
  for (const sp of subProducts) {
    if (!sp.dogma_valuejson) continue;
    try {
      const opt = JSON.parse(sp.dogma_valuejson);
      if (!UUID_REGEX.test(opt?.optionId ?? '')) continue;
      const img = opt?.thumbnail || opt?.withoutHornsUrl || opt?.withHornsUrl;
      if (img) return img;
    } catch {}
  }
  // Pass 2: variation (optionType 64) with any image url
  for (const sp of subProducts) {
    if (!sp.dogma_valuejson) continue;
    try {
      const opt = JSON.parse(sp.dogma_valuejson);
      if (opt?.optionType !== 64) continue;
      const img = opt?.thumbnail || opt?.withoutHornsUrl || opt?.withHornsUrl;
      if (img) return img;
    } catch {}
  }
  return null;
};

export const generateOrderAcknowledgementPDF = async (
  quote: IDataverseQuote,
  lineItems: IQuoteLineItem[],
  headerDates?: IPDFHeaderDates,
  documentTitle: string = 'Quotation',
) => {
  const doc = new jsPDF({ orientation: 'p', unit: 'pt', format: 'a4', compress: true });
  const totalPages = lineItems.length;

  // Pre-compress the VS logo once for reuse across all pages
  const logoDataUrl = await fetchImageAsDataUrl(vsLogo).then(
    async (raw) => raw ? compressImageForPdf(raw, 200, 0.5) : null
  ).catch(() => null);

  // Fetch quote image list once — blended template/window images stored at quote creation
  let quoteImageList: { fileName: string; imageUrl: string }[] = [];
  if (quote.quoteid) {
    try {
      const imgRes = await getQuoteImageApi(quote.quoteid);
      quoteImageList = imgRes?.images ?? [];
    } catch { /* no images — continue with product thumbnail */ }
  }

  for (let i = 0; i < lineItems.length; i++) {
    if (i > 0) doc.addPage();

    const lineItem = lineItems[i] as IQuoteLineItem;
    const isLastPage = (i === lineItems.length - 1);

    let subProducts: ISubQuoteProduct[] = [];
    try {
      const response = await getSubQuoteProductsApi(lineItem.quotedetailid);
      subProducts = response?.value || [];
    } catch (error) {
    }

    // Priority 1: stored blend/template image for this specific window
    // Images are named "offline-blend-{quotedetailId}.png" or similar
    let thumbnailDataUrl: string | null = null;
    const nonSignatureImages = quoteImageList.filter(
      (img) => !img.fileName?.toLowerCase().includes('signature')
    );
    const quoteImg =
      nonSignatureImages.find((img) => img.fileName?.toLowerCase().includes(lineItem.quotedetailid.toLowerCase())) ??
      nonSignatureImages[i] ??
      nonSignatureImages[0];

    if (quoteImg?.fileName && quote.quoteid) {
      const baseName = quoteImg.fileName.split('/').pop();
      if (baseName) {
        const raw = await getQuoteImageFileDataUrlApi(quote.quoteid, baseName);
        thumbnailDataUrl = raw ? await compressImageForPdf(raw, 480, 0.72) : null;
      }
    }

    // Priority 2: fall back to product thumbnail if no quote image
    if (!thumbnailDataUrl) {
      const thumbnailUrl = extractProductThumbnail(subProducts);
      let rawThumb = thumbnailUrl ? await fetchImageAsDataUrl(thumbnailUrl) : null;
      if (!rawThumb) rawThumb = await fetchProductThumbnailFromApi(lineItem._productid_value);
      thumbnailDataUrl = rawThumb ? await compressImageForPdf(rawThumb) : null;
    }

    await drawPage(doc, quote, lineItem, subProducts, i + 1, totalPages, isLastPage, headerDates, thumbnailDataUrl, documentTitle, logoDataUrl);
  }

  const fileName = `Order_Acknowledgement_${quote.quotenumber}_${dayjs().format('DD-MMM-YYYY')}.pdf`;
  doc.save(fileName);
};

async function drawPage(
  doc: jsPDF,
  quote: IDataverseQuote,
  lineItem: IQuoteLineItem,
  subProducts: ISubQuoteProduct[],
  pageNum: number,
  totalPages: number,
  isLastPage: boolean,
  headerDates?: IPDFHeaderDates,
  thumbnailDataUrl?: string | null,
  documentTitle: string = 'Quotation',
  logoDataUrl?: string | null,
) {
  doc.setTextColor(0, 0, 0);

  // Page number - top left
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text(`Page ${pageNum} of ${totalPages}`, 25, 40);

  // Title - centered
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text(documentTitle, 297, 40, { align: "center" });

  // Horizontal line under header
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.5);
  doc.line(25, 53, 570, 53);

  // Customer info box - first page only
  let configStartY = 65;
  if (pageNum === 1) {
    const boxBottomY = drawCustomerBox(doc, quote, headerDates, logoDataUrl);
    configStartY = boxBottomY + 10;
  }

  // Configuration section
  let y = drawConfiguration(doc, quote, lineItem, subProducts, configStartY, thumbnailDataUrl);

  // Last page additions — financials flow directly after spec table.
  // Add new page only if insufficient room remains.
  if (isLastPage) {
    if (y > 650) {
      doc.addPage();
      y = 65;
    }
    y = drawFinancials(doc, quote, y);
    drawSignature(doc, y + 15);
  } else {
    drawFooter(doc);
  }
}

function drawCustomerBox(doc: jsPDF, quote: IDataverseQuote, headerDates?: IPDFHeaderDates, logoDataUrl?: string | null): number {
  const x = 25;
  const boxY = 62;
  const w = 545;
  const divX = 250;
  const headerH = 18;
  const custDivX = divX + 120;

  // ── Pre-calculate content to determine dynamic box height ─────────────────
  const custName = quote.dogma_customer?.name
    || quote['_dogma_customer_value@OData.Community.Display.V1.FormattedValue']
    || quote['_customerid_value@OData.Community.Display.V1.FormattedValue']
    || 'N/A';

  const billingParts = (quote.dogma_customer?.address1_composite || '')
    .split(/\r?\n/).map(s => s.trim()).filter(Boolean).slice(0, 5);

  const deliveryParts = [
    quote.shipto_name,
    quote.shipto_line1,
    quote.shipto_line2,
    quote.shipto_line3,
    [quote.shipto_city, quote.shipto_stateorprovince].filter(Boolean).join(', '),
    quote.shipto_postalcode,
    quote.shipto_country,
  ].map(s => (s ?? '').trim()).filter(Boolean).slice(0, 5);
  const hasDelivery = deliveryParts.length > 0;

  const contact = quote.dogma_customer?.primarycontactid;
  const tel = contact?.telephone1 || quote.dogma_customer?.telephone1 || '';
  const email = contact?.emailaddress1 || '';

  // Max width available for middle column text (divX+8 … custDivX-8)
  const custColMaxW = custDivX - divX - 18;

  // Pre-wrap long lines so height calculation matches what will be drawn.
  // Set font to 8.5pt normal (used for most body text) before calling splitTextToSize.
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  const wrappedBilling = billingParts.map(p => doc.splitTextToSize(p, custColMaxW) as string[]);
  const billingLineCount = wrappedBilling.reduce((s, ls) => s + ls.length, 0);
  const wrappedDelivery = deliveryParts.map(p => doc.splitTextToSize(p, custColMaxW) as string[]);
  const deliveryLineCount = wrappedDelivery.reduce((s, ls) => s + ls.length, 0);

  // Height: headerH + gap(14) + name(12) + billing(n×10) + gap(4) + [contact(10)] + tel(10) + [email(10)]
  //         + [delivery section: gap(8) + label(10) + lines(n×10)] + bottom(30)
  let contentH = 12 + billingLineCount * 10 + 4;
  if (contact?.fullname) contentH += 10;
  contentH += 10; // tel
  if (email) contentH += 10;
  if (hasDelivery) contentH += 8 + 10 + deliveryLineCount * 10;
  const h = Math.max(headerH + 14 + contentH + 30, 155);

  // ── Draw box and dividers ─────────────────────────────────────────────────
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.5);
  doc.rect(x, boxY, w, h);
  doc.line(divX, boxY, divX, boxY + h);
  doc.line(x, boxY + headerH, x + w, boxY + headerH);

  // ── RIGHT HEADER ──────────────────────────────────────────────────────────
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Customer & Order Information', divX + (w - (divX - x)) / 2, boxY + 12, { align: 'center' });

  // ── LEFT SECTION — Victorian Sliders ─────────────────────────────────────
  let ly = boxY + headerH + 8;
  try {
    const logoSrc = logoDataUrl || vsLogo;
    doc.addImage(logoSrc, 'JPEG', x + 6, ly, 80, 36);
  } catch { /* logo missing — skip */ }
  ly += 42;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Victorian Sliders', x + 6, ly);

  ly += 12;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('Victorian House', x + 6, ly); ly += 10;
  doc.text('Parc Hendre Business Park', x + 6, ly); ly += 10;
  doc.text('Capel Hendre', x + 6, ly); ly += 10;
  doc.text('Carmarthenshire', x + 6, ly); ly += 10;
  doc.text('SA18 3SJ', x + 6, ly);
  ly += 13;
  doc.setFontSize(8.5);
  doc.text('Tel :  01269 846200      Fax:  01269 846201', x + 6, ly);

  // ── RIGHT SECTION — Customer info ─────────────────────────────────────────
  doc.line(custDivX, boxY + headerH, custDivX, boxY + h - 15);

  let ry = boxY + headerH + 14;

  // Customer name — clamp to column width
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  const nameMaxW = custColMaxW + 2; // slightly wider at 10pt bold
  const nameLines = doc.splitTextToSize(custName, nameMaxW) as string[];
  doc.text(nameLines[0] ?? custName, divX + 8, ry); // name: one line max, truncate if still long
  ry += 12;

  // Billing address — pre-wrapped
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  for (const lines of wrappedBilling) {
    doc.text(lines, divX + 8, ry);
    ry += 10 * lines.length;
  }
  ry += 4;

  // Contact / tel / email — clamp each to column width
  if (contact?.fullname) {
    const contactLines = doc.splitTextToSize(`Contact :  ${contact.fullname}`, custColMaxW) as string[];
    doc.text(contactLines, divX + 8, ry);
    ry += 10 * contactLines.length;
  }
  const telLines = doc.splitTextToSize(`Tel :  ${tel}`, custColMaxW) as string[];
  doc.text(telLines, divX + 8, ry);
  ry += 10 * telLines.length;
  if (email) {
    const emailLines = doc.splitTextToSize(`Email :  ${email}`, custColMaxW) as string[];
    doc.text(emailLines, divX + 8, ry);
    ry += 10 * emailLines.length;
  }

  // Delivery address — pre-wrapped
  if (hasDelivery) {
    ry += 8;
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bold');
    doc.text('Delivery Address :', divX + 8, ry);
    ry += 10;
    doc.setFont('helvetica', 'normal');
    for (const lines of wrappedDelivery) {
      doc.text(lines, divX + 8, ry);
      ry += 10 * lines.length;
    }
  }

  // ── Job details column (right-aligned) ────────────────────────────────────
  const labelX = custDivX + 10;
  const valueX = x + w - 10;

  ry = boxY + headerH + 14;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');

  doc.text('Job No :', labelX, ry);
  doc.text(quote.quotenumber || 'N/A', valueX, ry, { align: 'right' });

  if (headerDates?.evolutionJobNumber) {
    ry += 10;
    doc.text('Evolution Job No :', labelX, ry);
    doc.text(headerDates.evolutionJobNumber, valueX, ry, { align: 'right' });
  }

  ry += 10;
  doc.text('Production Date :', labelX, ry);
  const prodDate = headerDates?.productionDate ?? (quote.createdon ? dayjs(quote.createdon).format('DD-MMM-YYYY') : '');
  doc.text(prodDate, valueX, ry, { align: 'right' });

  ry += 10;
  doc.text('Expected Delivery Date :', labelX, ry);
  const deliveryDate = headerDates?.deliveryDate ?? '';
  doc.text(deliveryDate, valueX, ry, { align: 'right' });

  ry += 10;
  doc.text('Processed Date :', labelX, ry);
  const processedDate = headerDates?.processedDate ?? dayjs().format('DD-MMM-YYYY');
  doc.text(processedDate, valueX, ry, { align: 'right' });

  ry += 10;
  doc.text('Printed Date :', labelX, ry);
  const printedDate = headerDates?.printedDate ?? dayjs().format('DD-MMM-YYYY');
  doc.text(printedDate, valueX, ry, { align: 'right' });

  ry += 10;
  doc.text('Order Date :', labelX, ry);
  const orderDate = headerDates?.orderDate ?? (quote.createdon ? dayjs(quote.createdon).format('DD-MMM-YYYY') : '');
  doc.text(orderDate, valueX, ry, { align: 'right' });

  // ── Bottom row — Job Reference ─────────────────────────────────────────────
  const bottomY = boxY + h - 7;
  doc.line(x, bottomY - 10, x + w, bottomY - 10);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  const jobRef = quote.dogma_projectreference || 'N/A';
  doc.text(`Job Reference :  ${jobRef}`, divX + 8, bottomY);

  return boxY + h;
}

// ── Measurement extraction ────────────────────────────────────────────────────

interface WindowMeasurements {
  width: number | null;
  height: number | null;
  transomDrop: number | null;
  frameWidths: number[];   // per-frame widths for coupled windows
  sashOperation: SashOperationValue | null;
  frameSashOperations: (SashOperationValue | undefined)[];
}

function extractMeasurements(subProducts: ISubQuoteProduct[]): WindowMeasurements {
  const m: WindowMeasurements = { width: null, height: null, transomDrop: null, frameWidths: [], sashOperation: null, frameSashOperations: [] };

  const val = (sp: ISubQuoteProduct) => {
    const v = sp.dogma_value?.trim();
    if (!v) return null;
    const n = Number(v);
    return isNaN(n) ? null : n;
  };

  for (const sp of subProducts) {
    const name = (sp.dogma_name || '').toLowerCase();

    // Sash operation (Both Sashes Slide/Fixed, Bottom/Top Sash Fixed) — a
    // fixed sash can never provide fire egress, regardless of geometry.
    let sashOptionType: number | undefined;
    let sashResponseValue: string | undefined;
    try {
      const vj = JSON.parse(sp.dogma_valuejson || '') as any;
      sashOptionType = vj?.optionType;
      sashResponseValue = vj?.responseValue;
    } catch {}
    if (sashOptionType === OPTION_TYPE_SASH && sashResponseValue) {
      const opValue = sashResponseValue as SashOperationValue;
      let frameIdx: number | null = null;
      try {
        const qj = JSON.parse(sp.dogma_questionjson || '') as any;
        const responsePath: string = qj?.question?.responsePath ?? '';
        const fm = responsePath.match(/frame(\d+)$/i);
        if (fm) frameIdx = Number(fm[1]) - 1;
      } catch {}
      if (frameIdx !== null) {
        m.frameSashOperations[frameIdx] = opValue;
      } else {
        m.sashOperation = opValue;
      }
      continue;
    }

    const v = val(sp);
    if (v === null) continue;

    // Overall width
    if (name === 'width (mm)' || name === 'overall width (mm)' || name === 'width_(mm)') {
      m.width = v;
    }
    // Overall height
    else if (name.includes('overall height') || name === 'overall_height_(mm)') {
      m.height = v;
    }
    // Transom drop — optionType 5 or name match
    else if (name.includes('transom')) {
      m.transomDrop = v;
    }
    // Per-frame widths (coupled/bay): "Window 1 Width (mm)", "Window 2 Width (mm)"
    else if (/window\s*\d+\s*width/i.test(name) || /frame_?\d+_?width/i.test(name)) {
      const idx = parseInt(name.match(/\d+/)?.[0] || '1', 10) - 1;
      m.frameWidths[idx] = v;
    }
  }

  // If no per-frame widths, fall back to overall width
  if (m.frameWidths.length === 0 && m.width !== null) {
    m.frameWidths = [m.width];
  }

  // Backfill per-frame sash operation from the "same for all" answer.
  if (m.frameWidths.length > 0 && m.sashOperation) {
    m.frameWidths.forEach((_, i) => {
      if (m.frameSashOperations[i] === undefined) {
        m.frameSashOperations[i] = m.sashOperation!;
      }
    });
  }

  return m;
}

// ── Measurement diagram ───────────────────────────────────────────────────────

function drawMeasurementDiagram(
  doc: jsPDF,
  x: number, y: number, boxW: number, boxH: number,
  m: WindowMeasurements,
) {
  if (!m.width && !m.height) return;

  const pad = 28; // space for dimension labels
  const winX = x + pad;
  const winY = y + pad * 0.5;
  const winW = boxW - pad * 2;
  const winH = boxH - pad;

  // Window outer rectangle
  doc.setDrawColor(80, 80, 80);
  doc.setLineWidth(1.2);
  doc.rect(winX, winY, winW, winH);

  // Inner frame inset
  const ins = 4;
  doc.setLineWidth(0.4);
  doc.rect(winX + ins, winY + ins, winW - ins * 2, winH - ins * 2);

  // ── Transom line ────────────────────────────────────────────────────────
  let transomY: number | null = null;
  if (m.transomDrop !== null && m.height !== null && m.height > 0) {
    const ratio = m.transomDrop / m.height;
    transomY = winY + winH * ratio;
    doc.setDrawColor(80, 80, 80);
    doc.setLineWidth(0.8);
    doc.line(winX, transomY, winX + winW, transomY);
    // Inner transom line
    doc.setLineWidth(0.3);
    doc.line(winX + ins, transomY, winX + winW - ins, transomY);
  }

  // ── Per-frame dividers (coupled/bay) ────────────────────────────────────
  if (m.frameWidths.length > 1) {
    const totalFrameW = m.frameWidths.reduce((s, v) => s + v, 0);
    let cumX = winX;
    for (let i = 0; i < m.frameWidths.length - 1; i++) {
      cumX += winW * (m.frameWidths[i]! / totalFrameW);
      doc.setLineWidth(0.8);
      doc.line(cumX, winY, cumX, winY + winH);
    }
  }

  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.4);

  const arr = 3; // arrowhead size

  const drawArrow = (x1: number, y1: number, x2: number, y2: number) => {
    doc.line(x1, y1, x2, y2);
    // small arrowheads
    if (y1 === y2) {
      doc.line(x1, y1, x1 + arr, y1 - arr / 2); doc.line(x1, y1, x1 + arr, y1 + arr / 2);
      doc.line(x2, y2, x2 - arr, y2 - arr / 2); doc.line(x2, y2, x2 - arr, y2 + arr / 2);
    } else {
      doc.line(x1, y1, x1 - arr / 2, y1 + arr); doc.line(x1, y1, x1 + arr / 2, y1 + arr);
      doc.line(x2, y2, x2 - arr / 2, y2 - arr); doc.line(x2, y2, x2 + arr / 2, y2 - arr);
    }
  };

  // ── Width dimension (top) ───────────────────────────────────────────────
  if (m.width !== null) {
    const dimY = winY - 10;
    doc.line(winX, winY, winX, dimY + 3);
    doc.line(winX + winW, winY, winX + winW, dimY + 3);
    drawArrow(winX, dimY, winX + winW, dimY);
    doc.text(`${m.width}`, winX + winW / 2, dimY - 2, { align: 'center' });

    // Bottom width label too
    const botY = winY + winH + 10;
    doc.line(winX, winY + winH, winX, botY - 3);
    doc.line(winX + winW, winY + winH, winX + winW, botY - 3);
    drawArrow(winX, botY, winX + winW, botY);
    doc.text(`${m.width}`, winX + winW / 2, botY + 5, { align: 'center' });
  }

  // ── Overall height dimension (left) ─────────────────────────────────────
  if (m.height !== null) {
    const dimX = winX - 14;
    doc.line(winX, winY, dimX + 3, winY);
    doc.line(winX, winY + winH, dimX + 3, winY + winH);
    drawArrow(dimX, winY, dimX, winY + winH);
    doc.text(`${m.height}`, dimX - 3, winY + winH / 2, { align: 'right', baseline: 'middle' });
  }

  // ── Transom drop dimension (right) ──────────────────────────────────────
  if (transomY !== null && m.transomDrop !== null) {
    const dimX = winX + winW + 12;
    doc.line(winX + winW, winY, dimX - 3, winY);
    doc.line(winX + winW, transomY, dimX - 3, transomY);
    drawArrow(dimX, winY, dimX, transomY);
    doc.text(`${m.transomDrop}`, dimX + 3, winY + (transomY - winY) / 2, { baseline: 'middle' });

    // Lower sash height
    const lowerH = m.height ? m.height - m.transomDrop : null;
    if (lowerH !== null) {
      doc.line(winX + winW, transomY, dimX - 3, transomY);
      doc.line(winX + winW, winY + winH, dimX - 3, winY + winH);
      drawArrow(dimX, transomY, dimX, winY + winH);
      doc.text(`${lowerH}`, dimX + 3, transomY + (winY + winH - transomY) / 2, { baseline: 'middle' });
    }
  }

  // ── Per-frame width labels ───────────────────────────────────────────────
  if (m.frameWidths.length > 1 && m.width) {
    const totalFrameW = m.frameWidths.reduce((s, v) => s + v, 0);
    let cumX = winX;
    const labelY = winY + winH / 2;
    m.frameWidths.forEach((fw, i) => {
      const segW = winW * (fw / totalFrameW);
      doc.setFontSize(5.5);
      doc.text(`W${i + 1}: ${fw}`, cumX + segW / 2, labelY, { align: 'center' });
      cumX += segW;
    });
  }

  doc.setTextColor(0, 0, 0);
}

// ── Pricing breakdown (right side of image section) ──────────────────────────

/**
 * Draws a slice of the pricing breakdown starting at `startIdx`.
 * Returns the index of the next unrendered item (= priced.length when all done).
 * The Total row is only drawn when all items are rendered (nextIdx === priced.length).
 */
function drawPricingBreakdown(
  doc: jsPDF,
  subProducts: ISubQuoteProduct[],
  x: number, y: number, w: number, maxH: number,
  lineItem?: IQuoteLineItem,
  startIdx: number = 0,
  dealersCurrency?: IDataverseQuote['dogma_DealersCurrency'],
  exchangeRate: number = 1,
): number {
  const qty = lineItem?.quantity ?? 1;
  const fmt = (v: number) => formatDealerAmount(v, dealersCurrency, exchangeRate);

  const isFoilProfileSelected = subProducts.some((sp) => {
    try {
      const v = JSON.parse(sp.dogma_valuejson ?? '{}') as any;
      return v.optionType === 60 && isProfileColourFoilOption(v.responseValue ?? '');
    } catch { return false; }
  });
  const getSubProductName = (sp: ISubQuoteProduct): string => {
    try {
      const v = JSON.parse(sp.dogma_valuejson ?? '{}') as any;
      if (isFoilProfileSelected && v.optionType != null && FOIL_COLOUR_OPTION_TYPES.has(v.optionType)) {
        return FOIL_CHARGE_BREAKDOWN_LABEL;
      }
    } catch {}
    const optType = sp.dogma_optionname?.trim() || '';
    const itemName = sp.dogma_name?.trim() || '';
    const baseName = optType && itemName && optType !== itemName ? `${optType} - ${itemName}` : itemName || optType;
    try {
      const msg = JSON.parse(sp.dogma_questionjson ?? '');
      const rp: string = msg?.question?.responsePath ?? '';
      const m = rp.match(/frame(\d+)$/i);
      if (m) return `Frame ${m[1]} - ${baseName}`;
    } catch {}
    return baseName;
  };

  const priced = filterVariationItems(
    subProducts.filter(sp => ((sp.dogma_vssprice ?? sp.dogma_amount) ?? 0) !== 0)
  );
  if (priced.length === 0) return 0;

  const colName  = x;
  const colQty   = x + w - 130;
  const colGross = x + w - 95;
  const colDisc  = x + w - 52;
  const colNett  = x + w - 5;
  const rowH     = 10;

  // Header
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'bold');
  doc.setDrawColor(0); doc.setLineWidth(0.5);
  const hy = y + 8;
  doc.text('Pricing', colName, hy);
  doc.text('Window QTY', colQty, hy, { align: 'right' });
  doc.text('Gross', colGross, hy, { align: 'right' });
  doc.text('Disc.', colDisc,  hy, { align: 'right' });
  doc.text('Nett',  colNett,  hy, { align: 'right' });
  doc.line(x, y + 11, x + w, y + 11);

  let ry = y + 11 + rowH;
  let total = 0;
  let nextIdx = startIdx;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);

  for (let i = startIdx; i < priced.length; i++) {
    if (ry > y + maxH - rowH) break; // leave room for total row

    const sp = priced[i]!;
    let parsedValueJson: { resolverValue?: string; optionType?: number } = {};
    try { parsedValueJson = JSON.parse(sp.dogma_valuejson || ''); } catch {}
    const isFlatCharge = parsedValueJson.resolverValue === 'specials_foil_surchage' || parsedValueJson.optionType === 84;
    const name = getSubProductName(sp);
    const vssPrice = sp.dogma_vssprice ?? sp.dogma_amount ?? 0;
    const lineTotal = vssPrice * (isFlatCharge ? 1 : qty);
    const rawDiscount = (sp.dogma_vssdiscountamount ?? 0) * (isFlatCharge ? 1 : qty);
    const discountTotal = Math.min(rawDiscount, Math.max(0, lineTotal));
    const nettTotal = lineTotal - discountTotal;
    // Window QTY column must show the line item's own order quantity, not
    // dogma_quantity (a per-sub-product internal multiplier, e.g. metres of
    // profile) — that defaults to 1 for almost every sub-product regardless
    // of how many of this window were actually ordered, which is why the
    // column always read "1" even when the product line's real quantity was
    // higher.
    const unit = String(qty);
    const gross = fmt(lineTotal);
    const disc  = discountTotal > 0 ? fmt(discountTotal) : '';
    const nett  = fmt(nettTotal);

    const maxNameW = colQty - colName - 4;
    let displayName = name;
    while (doc.getTextWidth(displayName) > maxNameW && displayName.length > 4) {
      displayName = displayName.slice(0, -1);
    }
    if (displayName !== name) displayName += '…';

    doc.text(displayName, colName, ry);
    doc.text(unit,  colQty,  ry, { align: 'right' });
    doc.text(gross, colGross, ry, { align: 'right' });
    doc.text(disc,  colDisc,  ry, { align: 'right' });
    doc.text(nett,  colNett,  ry, { align: 'right' });

    total += lineTotal;
    ry += rowH;
    nextIdx = i + 1;
  }

  // Total row — always drawn after items
  const grossTotal = lineItem?.dogma_vsssubtotal ?? total;
  const discountLineTotal = lineItem?.dogma_vssdiscountamount ?? 0;
  const nettLineTotal = grossTotal - discountLineTotal;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.text('Total', colQty, ry + 3, { align: 'right' });
  doc.text(fmt(grossTotal), colGross, ry + 3, { align: 'right' });
  doc.text(discountLineTotal > 0 ? fmt(discountLineTotal) : '', colDisc, ry + 3, { align: 'right' });
  doc.text(fmt(nettLineTotal),  colNett,  ry + 3, { align: 'right' });
  doc.setFont('helvetica', 'normal');

  return nextIdx;
}

// ── drawConfiguration ─────────────────────────────────────────────────────────

function drawConfiguration(
  doc: jsPDF,
  quote: IDataverseQuote,
  lineItem: IQuoteLineItem,
  subProducts: ISubQuoteProduct[],
  startY: number,
  thumbnailDataUrl?: string | null,
): number {
  let y = startY;
  const dealersCurrency = quote.dogma_DealersCurrency;
  const exchangeRate = quote.dogma_exchangerate ?? dealersCurrency?.dogma_exchangerate ?? 1;

  const rowX = 25;
  const rowW = 545;
  const rowH = 26;
  const windowName = lineItem.dogma_windowname || '';
  const egressBadge = getEgressBadgeForPDF(subProducts);
  // Header needs two text lines when window name OR egress badge is present
  const dynamicRowH = (windowName || egressBadge) ? rowH + 12 : rowH;

  // ── Product header row ────────────────────────────────────────────────────
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(1.2);
  doc.rect(rowX, y, rowW, dynamicRowH);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  const prodName = lineItem.productname || 'ECO Slide Vertical Slider V3';
  doc.text(prodName, rowX + 15, y + 13);

  if (windowName) {
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text(windowName, rowX + 15, y + 25);
  }

  // ── Fire egress — top-right of header (matches spec screenshot) ──────────
  if (egressBadge) {
    const rightEdge = rowX + rowW - 8;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('FIRE EGRESS WINDOW', rightEdge, y + 13, { align: 'right' });
    const badgeLabel = egressBadge.compliant
      ? `COMPLIANT (${egressBadge.pass === 'tilting' ? 'Tilting' : egressBadge.pass === 'both' ? 'Sliding/Tilting' : 'Sliding'})`
      : 'NON-COMPLIANT';
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text(badgeLabel, rightEdge, y + 25, { align: 'right' });
  }

  y += dynamicRowH;

  // ── Shared box: image+annotations (left, optional) + pricing breakdown (right, always) ──
  {
    const measurements = thumbnailDataUrl ? extractMeasurements(subProducts) : null;

    const leftPad  = measurements?.height != null ? 32 : 8;
    const rightPad = (measurements?.transomDrop != null || measurements?.height != null) ? 38 : 8;
    const topPad   = measurements?.width != null ? 20 : 8;
    const botPad   = measurements?.width != null ? 22 : 8;

    const imgW = 110;
    const imgH = 120;

    // sectionH grows to fit all pricing rows but never overflows the current page
    const pricedCount = filterVariationItems(
      subProducts.filter(sp => ((sp.dogma_vssprice ?? sp.dogma_amount) ?? 0) !== 0)
    ).length;
    const pricingRequiredH = pricedCount * 10 + 11 + 20 + 8;
    const imageRequiredH = thumbnailDataUrl ? topPad + imgH + botPad + 4 : 140;
    const pageAvailH = Math.max(140, 841 - 50 - y - dynamicRowH); // cap to remaining page
    const sectionH = Math.min(Math.max(imageRequiredH, pricingRequiredH), pageAvailH);

    // Single bordered box spanning full row width
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.8);
    doc.rect(rowX, y, rowW, sectionH);

    // Image area width — 0 when no image so pricing takes full width
    const imgAreaW = thumbnailDataUrl ? leftPad + imgW + rightPad + 10 : 0;
    if (thumbnailDataUrl) {
      const m = measurements!;
      const imgCenterOffset = Math.max(0, (imgAreaW - leftPad - imgW - rightPad) / 2);
      doc.setLineWidth(0.4);
      doc.line(rowX + imgAreaW, y, rowX + imgAreaW, y + sectionH);

      const imgX = rowX + leftPad + imgCenterOffset + 10;
      const imgY = y + topPad;

      // Place product image (always JPEG after compression step) — fit (not
      // stretch) into the imgW x imgH box, preserving its natural aspect
      // ratio and centering it, so a window render that isn't the same
      // proportions as the box doesn't get squished/distorted.
      try {
        let drawW = imgW;
        let drawH = imgH;
        let drawX = imgX;
        let drawY = imgY;
        try {
          const props = doc.getImageProperties(thumbnailDataUrl);
          const naturalRatio = props.width / props.height;
          const boxRatio = imgW / imgH;
          if (naturalRatio > boxRatio) {
            drawW = imgW;
            drawH = imgW / naturalRatio;
          } else {
            drawH = imgH;
            drawW = imgH * naturalRatio;
          }
          drawX = imgX + (imgW - drawW) / 2;
          drawY = imgY + (imgH - drawH) / 2;
        } catch {
          /* fall back to the original stretched box if properties can't be read */
        }
        doc.addImage(thumbnailDataUrl, 'JPEG', drawX, drawY, drawW, drawH);
      } catch { /* skip */ }

      // Draw measurement annotations around the image
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.5);

      const arr = 2.5;
      const tick = (x1: number, y1: number, x2: number, y2: number) => doc.line(x1, y1, x2, y2);
      const arrowH = (x1: number, xMid: number, x2: number, ay: number) => {
        tick(x1, imgY - 4, x1, imgY + 4);
        tick(x2, imgY - 4, x2, imgY + 4);
        doc.line(x1, ay, x2, ay);
        doc.line(x1, ay, x1 + arr, ay - arr / 2); doc.line(x1, ay, x1 + arr, ay + arr / 2);
        doc.line(x2, ay, x2 - arr, ay - arr / 2); doc.line(x2, ay, x2 - arr, ay + arr / 2);
        doc.text(String(m.width), xMid, ay - 2, { align: 'center' });
      };
      const arrowV = (ax: number, y1: number, yMid: number, y2: number, label: string, align: 'left' | 'right') => {
        tick(ax - 4, y1, ax + 4, y1);
        tick(ax - 4, y2, ax + 4, y2);
        doc.line(ax, y1, ax, y2);
        doc.line(ax, y1, ax - arr / 2, y1 + arr); doc.line(ax, y1, ax + arr / 2, y1 + arr);
        doc.line(ax, y2, ax - arr / 2, y2 - arr); doc.line(ax, y2, ax + arr / 2, y2 - arr);
        const tx = align === 'right' ? ax + 3 : ax - 3;
        doc.text(label, tx, yMid, { baseline: 'middle', align: align === 'right' ? 'left' : 'right' });
      };

      if (m.width !== null) {
        const topArrowY = imgY - 8;
        arrowH(imgX, imgX + imgW / 2, imgX + imgW, topArrowY);
      }

      if (m.height !== null) {
        const ax = imgX - 14;
        tick(imgX, imgY, ax + 4, imgY);
        tick(imgX, imgY + imgH, ax + 4, imgY + imgH);
        doc.line(ax, imgY, ax, imgY + imgH);
        doc.line(ax, imgY, ax - arr / 2, imgY + arr); doc.line(ax, imgY, ax + arr / 2, imgY + arr);
        doc.line(ax, imgY + imgH, ax - arr / 2, imgY + imgH - arr); doc.line(ax, imgY + imgH, ax + arr / 2, imgY + imgH - arr);
        doc.text(String(m.height), ax - 3, imgY + imgH / 2, { baseline: 'middle', align: 'right' });
      }

      if (m.transomDrop !== null && m.height !== null) {
        const ax = imgX + imgW + 14;
        const transomY = imgY + imgH * (m.transomDrop / m.height);
        doc.setLineWidth(0.3);
        doc.line(imgX + imgW, transomY, ax - 6, transomY);
        doc.setLineWidth(0.5);
        arrowV(ax, imgY, imgY + (transomY - imgY) / 2, transomY, String(m.transomDrop), 'left');
        const lowerH = m.height - m.transomDrop;
        arrowV(ax + 10, transomY, transomY + (imgY + imgH - transomY) / 2, imgY + imgH, String(lowerH), 'left');
      }

      if (m.frameWidths.length > 1 && m.width) {
        const totalW = m.frameWidths.reduce((s, v) => s + v, 0);
        const labelY = imgY + imgH + botPad - 4;
        doc.setDrawColor(0, 70, 180);
        doc.setLineWidth(0.4);
        let cumX = imgX;
        m.frameWidths.forEach((fw, i) => {
          const segW = imgW * (fw / totalW);
          if (i > 0) doc.line(cumX, imgY + imgH, cumX, imgY + imgH + 5);
          doc.setFontSize(6.5);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(0, 70, 180);
          doc.text(`${fw}`, cumX + segW / 2, labelY, { align: 'center' });
          cumX += segW;
        });
      }

      doc.setTextColor(0, 0, 0);
      doc.setDrawColor(0, 0, 0);
    }

    // Pricing breakdown — always rendered, right of image divider (or full width)
    const pricingX = rowX + imgAreaW + 4;
    const pricingW = rowX + rowW - pricingX;
    if (pricingW > 80) {
      drawPricingBreakdown(doc, subProducts, pricingX, y + 4, pricingW, sectionH - 8, lineItem, 0, dealersCurrency, exchangeRate);
    }

    y += sectionH;
  }

  // ── Specifications table ──────────────────────────────────────────────────
  y = drawSpecsTable(doc, subProducts, y);

  return y + 20;
}


function drawSpecsTable(doc: jsPDF, subProducts: ISubQuoteProduct[], y: number): number {
  const x = 25;
  const w = 545;
  const midX = x + w / 2;
  const headerH = 20;
  const CHK_COL_PAD = 38;

  const parseValue = (value: string | undefined, fallbackName: string): string => {
    if (!value) return fallbackName;
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        const labels = parsed.map(item =>
          typeof item === 'object' && item !== null
            ? (item.label || item.name || item.value || String(item))
            : String(item)
        ).filter(Boolean);
        return labels.length > 0 ? labels.join(', ') : fallbackName;
      }
      if (typeof parsed === 'object' && parsed !== null) {
        return parsed.label || parsed.name || parsed.value || fallbackName;
      }
      return String(parsed);
    } catch { return value; }
  };

  const updatedSubProducts = filterVariationItems(
    subProducts.filter(p =>
      p.dogma_categoryname !== 'Surcharge' &&
      p.dogma_categoryname !== 'Additional Charges' &&
      getSubProductOptionType(p) !== OPTION_TYPE_EGRESS_COMPLAINT
    )
  );

  const groupedByCategory: Record<string, ISubQuoteProduct[]> = {};
  updatedSubProducts.forEach(product => {
    const cat = product.dogma_categoryname || 'Other';
    if (!groupedByCategory[cat]) groupedByCategory[cat] = [];
    groupedByCategory[cat].push(product);
  });

  // Extract "Frame N" from dogma_questionjson → question.responsePath
  const getFrameFromProduct = (product: ISubQuoteProduct): string | null => {
    try {
      const msg = JSON.parse(product.dogma_questionjson ?? '');
      const responsePath: string = msg?.question?.responsePath ?? '';
      const m = responsePath.match(/frame(\d+)$/i);
      if (m) return `Frame ${m[1]}`;
    } catch {}
    return null;
  };

  const allSpecs: Array<{ label: string; value: string; isCategory: boolean; isSubHeader: boolean }> = [];

  Object.entries(groupedByCategory).forEach(([categoryName, products]) => {
    allSpecs.push({ label: categoryName.toUpperCase(), value: '', isCategory: true, isSubHeader: false });

    // Group by (optionTypeName | step | frameLabel) — one row per question per frame, matches UI
    const seen = new Map<string, ISubQuoteProduct[]>();
    products.forEach(product => {
      const optionTypeName = product.dogma_optionname || product.dogma_name || 'Item';
      const step = product.dogma_step ?? 0;
      const frameLabel = getFrameFromProduct(product) ?? '';
      const key = `${optionTypeName}|${step}|${frameLabel}`;
      if (!seen.has(key)) seen.set(key, []);
      seen.get(key)!.push(product);
    });

    // Sort by (frameNumber, step) so all Frame 1 items come together, then Frame 2, etc.
    // Items with no frame label (non-per-frame questions) sort first (frameNum = -1).
    const sortedEntries = Array.from(seen.entries()).sort(([keyA], [keyB]) => {
      const partsA = keyA.split('|');
      const partsB = keyB.split('|');
      const stepA = parseInt(partsA[1] ?? '0') || 0;
      const stepB = parseInt(partsB[1] ?? '0') || 0;
      const frameLabelA = partsA[2] ?? '';
      const frameLabelB = partsB[2] ?? '';
      const frameNumA = frameLabelA ? (parseInt(frameLabelA.replace(/\D/g, '')) || 999) : -1;
      const frameNumB = frameLabelB ? (parseInt(frameLabelB.replace(/\D/g, '')) || 999) : -1;
      if (frameNumA !== frameNumB) return frameNumA - frameNumB;
      return stepA - stepB;
    });

    sortedEntries.forEach(([key, group]) => {
      const [optionTypeName = '', , frameLabel = ''] = key.split('|');
      const label = frameLabel ? `${frameLabel} - ${optionTypeName}` : (optionTypeName || 'Item');

      if (group.length > 1) {
        const hasAnyValue = group.some(p => p.dogma_value?.trim());
        if (!hasAnyValue) {
          // Multi-select answer group (e.g. "Frame 1 - Add Ons": Cill, Left
          // Jamb, Head, Right Jamb) — each product IS a selected option, not
          // a distinct field, so dogma_name is the answer itself and there's
          // no separate dogma_value to show. Combine into one row, matching
          // the config view.
          const combined = group.map(p => p.dogma_name).filter(Boolean).join(', ');
          if (combined) {
            allSpecs.push({ label, value: combined, isCategory: false, isSubHeader: false });
          }
        } else {
          // One row per product — never flatten a multi-field group (e.g.
          // Dimension's width/height/angle fields) into one joined string.
          // In this branch dogma_name is the FIELD's own name (e.g.
          // "Load-Bearing Bay Jacks (Optional)"), not a distinct answer — an
          // unfilled optional field has no dogma_value, and falling back to
          // dogma_name/label just echoes the field's own name as if it were
          // the entered value. Skip it.
          group.forEach(p => {
            const rawValue = p.dogma_value?.trim();
            if (!rawValue) return;
            const rowLabel = frameLabel ? `${frameLabel} - ${p.dogma_name || optionTypeName}` : (p.dogma_name || optionTypeName || 'Item');
            allSpecs.push({ label: rowLabel, value: rawValue, isCategory: false, isSubHeader: false });
          });
        }
      } else {
        const p = group[0];
        if (!p) return;
        const value = p.dogma_value?.trim() || p.dogma_name || parseValue(p.dogma_value, 'Item');
        // "Cill Horn Size" alone hides WHICH field the value is for
        // ("Each Side Size (mm)") — append it, scoped to just this option.
        const rowLabel =
          optionTypeName === 'Cill Horn Size' && p.dogma_name
            ? (frameLabel ? `${frameLabel} - ${optionTypeName} - ${p.dogma_name}` : `${optionTypeName} - ${p.dogma_name}`)
            : label;
        allSpecs.push({ label: rowLabel, value, isCategory: false, isSubHeader: false });

        // Standard Hardware Type bundles a fixed set of hardware items,
        // described in the selected option's own `description` text rather
        // than as separate sub-products — surface it as its own row.
        if (optionTypeName === 'Hardware Type' && value.trim().toLowerCase() === 'standard') {
          try {
            const vj = JSON.parse(p.dogma_valuejson || '');
            const description: string | undefined = vj?.description;
            if (description?.trim()) {
              allSpecs.push({ label: 'Hardware Item', value: description.trim(), isCategory: false, isSubHeader: false });
            }
          } catch {}
        }
      }
    });
  });

  // ── Layout constants ──────────────────────────────────────────────────────
  const LABEL_OFF  = 8;
  const VAL_OFF    = 100;
  const CHK_PAD   = CHK_COL_PAD;
  const LINE_H    = 10;
  const CAT_H     = 13;
  const CAT_BEFORE = 3;
  const ROW_PAD   = 2;

  const lLabelX = x + LABEL_OFF;
  const lValX   = x + VAL_OFF;
  const lChkX   = midX - CHK_PAD;
  const lValW   = lChkX - lValX - 4;

  const rLabelX = midX + LABEL_OFF;
  const rValX   = midX + VAL_OFF;
  const rChkX   = x + w - CHK_PAD;
  const rValW   = rChkX - rValX - 4;

  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');

  // Pre-compute height of each spec entry (used for chunking and rendering)
  const specH = (spec: typeof allSpecs[0], valW: number, labelW: number): number => {
    if (spec.isCategory) return CAT_H + CAT_BEFORE;
    const valLines: string[] = doc.splitTextToSize(spec.value || ' ', valW);
    const labelLines: string[] = doc.splitTextToSize(spec.label || ' ', labelW);
    return Math.max(valLines.length, labelLines.length) * LINE_H + ROW_PAD;
  };

  const calcColH = (specs: typeof allSpecs, labelX: number, valX: number, valW: number): number => {
    const labelW = valX - labelX - 3;
    let h = 10;
    specs.forEach((spec, i) => {
      if (spec.isCategory && i === 0) h += CAT_H;
      else h += specH(spec, valW, labelW);
    });
    return h;
  };

  // Page geometry
  const PAGE_H     = 841;
  const BOTTOM_PAD = 50;
  const TOP_Y      = 65;
  const MAX_Y      = PAGE_H - BOTTOM_PAD;     // = 791
  const TABLE_OH   = headerH + 14;            // overhead per chunk (header + bottom pad) = 34
  const MIN_AVAIL  = 80;                       // don't start a chunk if less than this fits

  // If too little room remains on current page, move to a new page now
  if (MAX_Y - y - TABLE_OH < MIN_AVAIL) {
    doc.addPage();
    y = TOP_Y;
  }

  // Available content height on a full fresh page
  const FULL_AVAIL = MAX_Y - TOP_Y - TABLE_OH;

  const chunkBy = (items: typeof allSpecs, avail: number): number => {
    let bestN = 1; // at least 1 item always
    for (let n = 1; n <= items.length; n++) {
      const slice = items.slice(0, n);
      const mid = Math.ceil(slice.length / 2);
      const lH = calcColH(slice.slice(0, mid), lLabelX, lValX, lValW);
      const rH = calcColH(slice.slice(mid), rLabelX, rValX, rValW);
      if (Math.max(lH, rH) <= avail) { bestN = n; } else { break; }
    }
    return bestN;
  };

  const chunks: (typeof allSpecs)[] = [];
  let remaining = [...allSpecs];
  let isFirst = true;
  while (remaining.length > 0) {
    // First chunk: use whatever is available at current y (already guaranteed ≥ MIN_AVAIL)
    const avail = isFirst ? (MAX_Y - y - TABLE_OH) : FULL_AVAIL;
    const n = chunkBy(remaining, avail);
    chunks.push(remaining.slice(0, n));
    remaining = remaining.slice(n);
    isFirst = false;
  }

  // Render each chunk
  let firstChunk = true;
  for (const chunk of chunks) {
    if (!firstChunk) {
      doc.addPage();
      y = TOP_Y;
    }
    firstChunk = false;

    const midPoint = Math.ceil(chunk.length / 2);
    const leftSpecs  = chunk.slice(0, midPoint);
    const rightSpecs = chunk.slice(midPoint);
    const contentH = Math.max(calcColH(leftSpecs, lLabelX, lValX, lValW), calcColH(rightSpecs, rLabelX, rValX, rValW));
    const h = headerH + contentH + 14;

    // Draw header
    doc.setFillColor(0, 0, 0);
    doc.rect(x, y, w, headerH, 'F');
    doc.line(x, y + headerH, x + w, y + headerH);
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Specification', x + 8, y + 13);
    doc.setFontSize(7.5);
    doc.text('Chk', midX - CHK_COL_PAD, y + 13);
    doc.text('Chk', x + w - CHK_COL_PAD, y + 13);
    doc.setTextColor(0, 0, 0);

    // Draw box and divider
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    doc.rect(x, y, w, h);
    doc.line(midX, y, midX, y + h);

    const renderCol = (specs: typeof allSpecs, labelX: number, valX: number, chkX: number, valW: number) => {
      let cy = y + headerH + 10;
      specs.forEach((spec, i) => {
        if (spec.isCategory) {
          if (i > 0) cy += CAT_BEFORE;
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(7.5);
          doc.text(spec.label, labelX, cy);
          cy += CAT_H;
        } else {
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(7.5);
          const labelMaxW = valX - labelX - 3;
          const labelLines: string[] = doc.splitTextToSize(spec.label || '', labelMaxW);
          const valLines: string[] = doc.splitTextToSize(spec.value || '', valW);
          const rowLines = Math.max(labelLines.length, valLines.length);
          labelLines.forEach((line: string, li: number) => doc.text(line, labelX, cy + li * LINE_H));
          valLines.forEach((line: string, li: number) => doc.text(line, valX, cy + li * LINE_H));
          doc.text('[   ]', chkX, cy);
          cy += rowLines * LINE_H + ROW_PAD;
        }
      });
    };

    renderCol(leftSpecs,  lLabelX, lValX, lChkX, lValW);
    renderCol(rightSpecs, rLabelX, rValX, rChkX, rValW);

    y = y + h;
  }

  return y;
}


function drawFinancials(doc: jsPDF, quote: IDataverseQuote, y: number): number {
  y += 10;

  const dealersCurrency = quote.dogma_DealersCurrency;
  const exchangeRate = quote.dogma_exchangerate ?? dealersCurrency?.dogma_exchangerate ?? 1;
  const fmt = (v: number) => formatDealerAmount(v, dealersCurrency, exchangeRate);

  const FIN_LH = 13;

  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.5);
  doc.rect(80, y, 220, 28);
  doc.rect(80, y + 32, 220, 28);

  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text("Delivery Terms", 90, y + 18);
  doc.text("Payment Terms", 90, y + 50);

  const lx = 360;
  const vx = 545;

  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");

  const subtotal = quote.dogma_vsssubtotal ?? 0;
  let fy = y + FIN_LH - 8;
  doc.text("Subtotal", lx, fy);
  doc.text(fmt(subtotal), vx, fy, { align: "right" });

  const discountAmount = quote.dogma_vssdiscountamount ?? 0;
  const discountPct = quote.dogma_vssdiscountpercentage;
  const discountLabel = discountPct ? `Discount Amount (${discountPct}%)` : "Discount Amount";
  fy += FIN_LH;
  doc.text(discountLabel, lx, fy);
  doc.text(discountAmount > 0 ? `-${fmt(discountAmount)}` : fmt(0), vx, fy, { align: "right" });

  const afterDiscount = subtotal - discountAmount;
  fy += FIN_LH;
  doc.text("After Discount", lx, fy);
  doc.text(fmt(afterDiscount), vx, fy, { align: "right" });

  const deliveryCharge = quote.freightamount ?? 0;
  if (deliveryCharge > 0) {
    fy += FIN_LH;
    doc.text("Delivery Charge", lx, fy);
    doc.text(fmt(deliveryCharge), vx, fy, { align: "right" });
  }

  const vat = quote.dogma_vssvatamount ?? 0;
  const vatPercentage = quote.dogma_vssvatpercentage ?? quote.dogma_vatpercentage ?? dealersCurrency?.dogma_vatpercentage ?? 20;
  fy += FIN_LH;
  doc.text(`VAT @ ${vatPercentage}%`, lx, fy);
  doc.text(fmt(vat), vx, fy, { align: "right" });

  const totalInclVat = quote.dogma_vsstotalamount ?? 0;
  fy += FIN_LH;
  doc.setFont("helvetica", "bold");
  doc.text("Total (Incl. VAT)", lx, fy);
  doc.text(fmt(totalInclVat), vx, fy, { align: "right" });

  doc.setFont("helvetica", "normal");
  const deposit = 0.0;
  fy += FIN_LH;
  doc.text("Deposit", lx, fy);
  doc.text(fmt(deposit), vx, fy, { align: "right" });

  const balance = totalInclVat - deposit;
  fy += FIN_LH;
  doc.text("Balance", lx, fy);
  doc.text(fmt(balance), vx, fy, { align: "right" });

  return fy + 12;
}

function drawSignature(doc: jsPDF, y: number) {
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(0, 0, 0);
  doc.text(
    "I confirm the details of this order are correct as detailed in this document and agree to Victorian Slider's Terms and Conditions.",
    25,
    y,
  );

  doc.text("Signed", 25, y + 18);
  doc.line(75, y + 18, 250, y + 18);

  doc.text("Dated", 310, y + 18);
  doc.line(350, y + 18, 525, y + 18);
}

function drawFooter(doc: jsPDF) {
  doc.setFontSize(7);
  doc.setTextColor(100, 100, 100);
  doc.text('Continued ....', 530, 820);
}
